import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, number } from "../../../config/constants";
import { useWorkflowContext } from "../WorkflowContextProvider";
import AddVariablePopup from "./AddVariablePopup";
import CustomWorkflowVariables from "./ExistingWorkflowVariables";
import VariableTypes from "./VariableTypes";
import VariableConfiguration from "./VariablesConfiguration/VariableConfiguration";
import { useVariablesContext } from "./context/variableContext";
import { useSelectedVariableValues } from "./hooks/workflowVariables.hooks";
import "./workflowVariable.scss";
import { WORKFLOW_VARIABLE_LABELS } from "./workflowVariables.constants";

/**
 * WorkflowVariables component for managing workflow variables
 * @author Abhishek Dhillon
 * @parent WorkflowContextProvider
 * @returns {JSX.Element} The WorkflowVariables component
 */
const WorkflowVariables = ({ isFormVariable, usedWorkflowVariableIds, workflowId, projectId, searchTerm = "", formData, formId }) => {
  const { toggleVariableList, selectedVariable, setVariableTypeRef, setVariableType, setSelectedVariable, customWorkflowVariables } =
    useWorkflowContext();
  const { setSelectedVariableValues } = useSelectedVariableValues();
  const { setHasVarChanges } = useVariablesContext();

  const addButtonRef = useRef(null);

  useEffect(() => {
    return () => {
      setSelectedVariable(null);
    };
  }, []);

  /**
   * Sorts workflow variables by type priority (dropdown, date, text, number) and then alphabetically
   * Same logic as in ExistingWorkflowVariables component
   * @param {Array} variables - Array of workflow variables to sort
   * @returns {Array} Sorted array of variables
   */
  const sortWorkflowVariables = (variables) => {
    if (!variables) return [];
    const typePriority = {
      dropdown: number.ZERO,
      date: number.ONE,
      text: number.TWO,
      number: number.THREE,
    };
    return [...variables].sort((a, b) => {
      const typeA = a?.type;
      const typeB = b?.type;
      const priorityA = typePriority[typeA] ?? Number.MAX_SAFE_INTEGER;
      const priorityB = typePriority[typeB] ?? Number.MAX_SAFE_INTEGER;
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return (a?.name || "").localeCompare(b?.name || "");
    });
  };

  /**
   * Auto-select first variable when Custom Chips is clicked and variables are available
   * Selects the first variable from the sorted list (by type priority and name)
   * Only applies when not in form variable mode
   */
  useEffect(() => {
    if (!isFormVariable && !selectedVariable && customWorkflowVariables?.length > 0 && !toggleVariableList) {
      const sortedVariables = sortWorkflowVariables(customWorkflowVariables);
      const firstVariable = sortedVariables[0];
      setSelectedVariable(firstVariable);
      setSelectedVariableValues(firstVariable, firstVariable?.name);
      setHasVarChanges(false);
    }
  }, [customWorkflowVariables, isFormVariable, toggleVariableList]);

  /** Opens Add Chip popup (Field Type + name) for workflow or form chip context */
  const openAddChipPopup = () => {
    setVariableType(WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS);
    setVariableTypeRef({ visible: true, ref: addButtonRef.current, type: WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS, isFormVariable });
    setSelectedVariable(null);
  };

  return (
    <React.Fragment>
      <div className='row'>
        <div className='col-md-12'>
          <div className='d-flex justify-content-between align-items-center mt-2'>
            {!isFormVariable ? (
              <div className='form-sub-heading'>{WORKFLOW_VARIABLE_LABELS.EXISTING_FIELDS}</div>
            ) : (
              <div className='form-sub-heading font-weight-bold'>{WORKFLOW_VARIABLE_LABELS.CHIPS}</div>
            )}
            <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
              <button
                ref={addButtonRef}
                type='button'
                className={`btn btn-sm btn-tertiary d-flex align-items-center ${isFormVariable ? "mr-2" : ""}`}
                onClick={openAddChipPopup}
                title={WORKFLOW_VARIABLE_LABELS.ADD_CHIP}>
                <span className='app-icon-medium d-flex align-items-center justify-content-center'>{icon.ADD}</span>
                <span className='ml-1'>{WORKFLOW_VARIABLE_LABELS.ADD_CHIP}</span>
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className={`row ${isFormVariable ? "" : "mt-2"}`}>
        <div className={` ${isFormVariable ? "col-md-12" : "col-md-4 border-right"}`}>
          {toggleVariableList ? (
            <VariableTypes />
          ) : (
            <CustomWorkflowVariables
              isFormVariable={isFormVariable}
              usedWorkflowVariableIds={usedWorkflowVariableIds}
              workflowIdParam={workflowId}
              projectId={projectId}
              searchTerm={searchTerm}
            />
          )}
        </div>
        {!isFormVariable && (
          <div className='col-md-8'>
            {selectedVariable ? (
              <VariableConfiguration
                isFormVariable={isFormVariable}
                formData={formData}
                formId={formId}
                showClickToRemoveFromFormBanner={false}
              />
            ) : (
              <div className='form-sub-heading'>{WORKFLOW_VARIABLE_LABELS.NO_CUSTOM_VARIABLES}</div>
            )}
          </div>
        )}
      </div>
      <AddVariablePopup />
    </React.Fragment>
  );
};

export default WorkflowVariables;
